import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useProfiles } from "../profiles/ProfileContext";
import { Button } from "../components/ui/Button";
import { PageHeader, Panel } from "../components/Surface";
import { StatusLabel } from "../components/ListTable";
import { Alert, type AlertKind } from "../components/Alert";
import { Icon } from "../app/Icon";
import { inputClass } from "../components/FormKit";
import { labelize } from "../lib/format";
import { FunctionsError } from "../lib/functions-client";
import {
  getVerificationStatus, submitCredential, startIdentity, syncIdentity, uploadToSignedUrl, isNotConfigured, checkAuthority,
  type VerificationStatus,
} from "../lib/verification";

const AUTHORITY_TYPES = ["company", "broker_dispatcher"];
const AUTHORITY_LABEL: Record<string, string> = {
  active: "Active authority", inactive: "Not authorized", not_found: "Not found in FMCSA",
  error: "Check failed", not_checked: "Not checked",
};

const DOC_TYPES = ["cdl", "dot_authority", "mc_authority", "identity_document"];

export function Verification() {
  const { active, profiles } = useProfiles();
  const [status, setStatus] = useState<VerificationStatus | null>(null);
  const [docType, setDocType] = useState(DOC_TYPES[0]);
  const [file, setFile] = useState<File | null>(null);
  const [msg, setMsg] = useState<{ text: string; kind: AlertKind } | null>(null);
  const [busy, setBusy] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const showError = (text: string) => setMsg({ text, kind: "error" });
  const showSuccess = (text: string) => setMsg({ text, kind: "success" });

  async function refresh(): Promise<VerificationStatus> {
    const s = await getVerificationStatus();
    setStatus(s);
    return s;
  }

  // On load — and when we land back here from Stripe — reconcile a still-"processing" identity
  // against Stripe directly, so verification completes even if the webhook is delayed.
  async function reconcile() {
    setSyncing(true);
    try {
      await syncIdentity();
      await refresh();
    } catch {
      /* best-effort; the webhook will still update it */
    } finally {
      setSyncing(false);
    }
  }

  useEffect(() => {
    void refresh().then((s) => {
      if (s.identity.status === "processing") void reconcile();
    });
  }, []);

  async function onStartIdentity() {
    setMsg(null);
    try {
      const res = await startIdentity();
      if (res.url) window.location.href = res.url;
      else showError("Verification started, but no redirect was returned. Please try again or contact support.");
    } catch (e) {
      if (isNotConfigured(e)) {
        showError("Identity verification isn't enabled yet. Please contact support.");
      } else if (e instanceof FunctionsError && e.status === 502 && e.message) {
        // The provider (Stripe Identity) rejected the request — surface its reason so it's
        // actionable (e.g. "activate Stripe Identity" on a live account) instead of a dead end.
        showError(`Could not start verification: ${e.message}`);
      } else {
        showError("Could not start verification. Please try again in a moment or contact support.");
      }
    }
  }

  async function onSubmit() {
    if (!active || !file) return;
    setBusy(true);
    setMsg(null);
    try {
      const { upload_url } = await submitCredential(active.id, docType);
      if (upload_url) await uploadToSignedUrl(upload_url, file);
      setFile(null);
      await refresh();
      showSuccess("Submitted for review.");
    } catch {
      showError("Could not submit the document. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function onCheckAuthority() {
    if (!active) return;
    setAuthBusy(true);
    setMsg(null);
    try {
      await checkAuthority(active.id);
      await refresh();
      showSuccess("Authority check complete.");
    } catch (e) {
      showError(
        isNotConfigured(e)
          ? "FMCSA authority checks aren't enabled yet."
          : "Could not check authority — add a DOT or MC number to this profile, or try again later.",
      );
    } finally {
      setAuthBusy(false);
    }
  }

  const identityStatus = status?.identity.status ?? "not started";
  const identityVerified = identityStatus === "verified";
  const showAuthority = active && AUTHORITY_TYPES.includes(active.type);
  const authority = status?.authority.find((a) => a.profile_id === active?.id);

  return (
    <div className="mx-auto grid w-full max-w-2xl gap-6">
      <PageHeader title="Verification" subtitle="Verify your identity and credentials to post, apply, and bid." />

      {msg && <Alert kind={msg.kind} onDismiss={() => setMsg(null)}>{msg.text}</Alert>}

      {/* Identity hero */}
      <div className="grid justify-items-center gap-1 rounded-card border border-line bg-surface p-8 text-center">
        <span className={`grid h-16 w-16 place-items-center rounded-full border-2 ${identityVerified ? "border-verified text-verified" : "border-verified/40 text-verified"}`}>
          <Icon name="verify" className="h-8 w-8" />
        </span>
        <h2 className="mt-3 font-display text-2xl font-black tracking-tight">{identityVerified ? "You're verified" : "Verify it's really you"}</h2>
        <p className="max-w-sm text-sm text-muted">A quick, secure ID + selfie check by Stripe. We never store your ID images.</p>

        <div className="my-4 h-px w-full max-w-sm bg-line" />
        <p className="font-mono text-sm"><span className="text-muted">STATUS: </span><StatusLabel status={identityStatus} /></p>

        {!identityVerified && (
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Button onClick={onStartIdentity}>{identityStatus === "processing" ? "Restart verification" : "Start verification"}</Button>
            {identityStatus === "processing" && (
              <Button variant="ghost" onClick={reconcile} disabled={syncing} data-testid="check-status">
                {syncing ? "Checking…" : "Check status"}
              </Button>
            )}
            <Link to="/"><Button variant="ghost">Do this later</Button></Link>
          </div>
        )}
        <p className="mt-4 flex items-center gap-2 font-mono text-[11px] text-muted">
          <Icon name="security" className="h-3.5 w-3.5" /> Secure · Private · Encrypted · Powered by Stripe
        </p>
      </div>

      {/* What happens next */}
      {!identityVerified && (
        <div className="grid gap-3 rounded-card border border-line bg-surface p-4 sm:grid-cols-2">
          <div className="rounded-lg border border-verified/30 bg-verified-bg/40 p-3 font-mono text-xs">
            <p className="font-bold text-verified">IDENTITY VERIFIED ✓</p>
            <p className="mt-1 text-muted">→ Continue to Home</p>
          </div>
          <div className="rounded-lg border border-danger/30 bg-danger/5 p-3 font-mono text-xs">
            <p className="font-bold text-danger">COULDN'T VERIFY</p>
            <p className="mt-1 text-muted">→ Retry · Support</p>
          </div>
        </div>
      )}

      {/* Credentials */}
      <Panel title={`Credentials · ${active ? labelize(active.type) : "no active profile"}`}>
        {profiles.length === 0 ? (
          <p className="text-sm text-muted">Create a profile first.</p>
        ) : (
          <>
            <p className="text-sm text-muted">Upload a CDL, DOT/MC authority, or ID document for admin review.</p>
            <div className="flex flex-wrap items-center gap-2">
              <select className={inputClass} value={docType} onChange={(e) => setDocType(e.target.value)} aria-label="Document type">
                {DOC_TYPES.map((d) => <option key={d} value={d}>{labelize(d)}</option>)}
              </select>
              <input type="file" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              <Button onClick={onSubmit} disabled={busy || !active || !file}>{busy ? "Submitting…" : "Submit for review"}</Button>
            </div>
          </>
        )}
        {status && status.credentials.length > 0 && (
          <ul className="grid gap-2 border-t border-line pt-3">
            {status.credentials.map((c) => (
              <li key={c.id} className="flex items-center justify-between font-mono text-sm">
                <span>{labelize(c.doc_type)}</span>
                <StatusLabel status={c.status} />
              </li>
            ))}
          </ul>
        )}
      </Panel>

      {/* FMCSA operating authority (company / broker profiles) */}
      {showAuthority && (
        <Panel title="Operating authority · FMCSA">
          <p className="text-sm text-muted">
            Verify your {active?.type === "broker_dispatcher" ? "MC" : "DOT/MC"} number against FMCSA to show
            counterparties your authority is active. Add the number on your profile first.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={onCheckAuthority} disabled={authBusy}>
              {authBusy ? "Checking…" : authority && authority.status !== "not_checked" ? "Re-check authority" : "Check authority"}
            </Button>
            {authority && authority.status !== "not_checked" && (
              <span className="font-mono text-sm">
                <StatusLabel status={authority.status === "active" ? "verified" : authority.status === "error" ? "processing" : "rejected"} />
                <span className="ml-2 text-muted">{AUTHORITY_LABEL[authority.status]}</span>
              </span>
            )}
          </div>
          {authority && authority.status !== "not_checked" && authority.status !== "error" && (
            <dl className="grid gap-1 border-t border-line pt-3 font-mono text-xs text-muted sm:grid-cols-2">
              {authority.legal_name && <div><dt className="inline text-muted">Legal name: </dt><dd className="inline text-ink">{authority.legal_name}</dd></div>}
              {authority.dot_number && <div><dt className="inline text-muted">DOT: </dt><dd className="inline text-ink">{authority.dot_number}</dd></div>}
              {authority.mc_number && <div><dt className="inline text-muted">MC: </dt><dd className="inline text-ink">{authority.mc_number}</dd></div>}
              {authority.safety_rating && <div><dt className="inline text-muted">Safety rating: </dt><dd className="inline text-ink">{authority.safety_rating}</dd></div>}
            </dl>
          )}
        </Panel>
      )}
    </div>
  );
}
